import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { Download } from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import ActionFormDrawer from '../../performance-actions/ActionFormDrawer';
import { Badge } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { Note } from '../../../components/performance/evaluation/EvaluationParts';
import {
  ONE_ON_ONES_PATH, downloadBlob, formatNum, formatSigned, gapTone, profileLink,
} from '../../../components/performance/evaluation/evaluationAnalytics';
import {
  NS, createAction, exportEmployees, fetchEmployees,
} from '../config';

// E06 alt alan — çalışan tablosu: öz, yönetici, fark (yönetici − öz), değerlendirme hali, 360 kapsaması (yalnız sayı) ve
// görüşme/teyit durumu. Liste = EmployeesPage aynası; pano sayfasında tablo kendi içinde KAYMAZ. Fark süzgeci, saçılım kovası
// ya da fark sıralaması seçiliyken yalnız eş puanı olan kişiler listelenir (fark yalnız onlarda tanımlı). Satır → E14 görüşme
// sekmesi. Beklenti görüşmesi: İK → aksiyon (mevcut aksiyon akışı), yönetici → 1:1 görüşmeler.
const Wrap = styled.div`
  grid-column: span 12;
  min-width: 0;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const GapText = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ $color }) => $color};
`;

const SORT_OF_COLUMN = { fullName: 'employee', gap: 'gap' };
const TONE = { close: 'info', managerHigher: 'success', selfHigher: 'danger' };

export default function ReviewEmployees({
  filters, screen, bins, isHr, pairOnly,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const lang = i18n.language;
  const resetKey = JSON.stringify([filters, screen, bins]);
  const [table, setTable] = useState({ resetKey, pageNumber: 1, pageSize: 10, sort: null, order: 'asc' });
  const [actionFor, setActionFor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const effective = table.resetKey === resetKey ? table : { resetKey, pageNumber: 1, pageSize: table.pageSize, sort: null, order: 'asc' };
  const tableKey = JSON.stringify(effective);
  const panel = usePanel(() => fetchEmployees(filters, screen, bins, {
    sort: effective.sort, order: effective.order, pageNumber: effective.pageNumber, pageSize: effective.pageSize,
  }), [tableKey], !!filters.periodId);
  const rows = panel.data?.data || [];
  const total = panel.data?.totalRowCount || 0;

  const onTableChanged = (action, state) => {
    const sortName = state.sortOrder?.name;
    setTable({
      resetKey,
      pageNumber: (state.page || 0) + 1,
      pageSize: state.rowsPerPage || 10,
      sort: sortName ? SORT_OF_COLUMN[sortName] || null : null,
      order: state.sortOrder?.direction || 'asc',
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportEmployees(filters, screen, bins, { sort: effective.sort, order: effective.order });
      downloadBlob(res, 'degerlendirme-farklari.xlsx');
    } finally {
      setExporting(false);
    }
  };

  // AI03 aksiyon türü (beklenti görüşmesi) — P7a'nın aksiyon kataloğundaki `expectation_meeting`; kullanıcı çekmecede değiştirebilir.
  const actionDefaults = useMemo(() => (actionFor ? {
    actionType: 'expectation_meeting',
    title: t(`${NS}.expectationTalk.title`, { name: actionFor.fullName }),
    periodId: filters.periodId,
    linkedEmployeeId: actionFor.employeeId,
  } : undefined), [actionFor, filters.periodId, t]);

  const rowOf = (meta) => rows[meta.rowIndex] || {};
  const dash = '-';
  const score = (v) => (v === null || v === undefined ? dash : formatNum(v, lang));
  const columns = [
    {
      name: 'fullName',
      label: t(`${NS}.col.employee`),
      options: {
        sort: true, filter: false,
        customBodyRender: (v, meta) => (
          <Link to={profileLink(rowOf(meta).employeeId, filters.periodId, 'meetings')} onClick={(e) => e.stopPropagation()} data-link="profile">{v}</Link>
        ),
      },
    },
    { name: 'departmentName', label: t(`${NS}.col.department`), options: { sort: false, filter: false, customBodyRender: (v) => v || dash } },
    { name: 'titleName', label: t(`${NS}.col.title`), options: { sort: false, filter: false, customBodyRender: (v) => v || dash } },
    { name: 'selfScore', label: t(`${NS}.col.self`), options: { sort: false, filter: false, customBodyRender: score } },
    { name: 'managerScore', label: t(`${NS}.col.manager`), options: { sort: false, filter: false, customBodyRender: score } },
    {
      name: 'gap',
      label: t(`${NS}.col.gap`),
      options: {
        sort: true, filter: false,
        customBodyRender: (v, meta) => {
          if (v === null || v === undefined) return dash;
          const r = rowOf(meta);
          return (
            <span>
              <GapText $color={gapTone(theme, v)} data-gap={r.employeeId}>{formatSigned(v, lang)}</GapText>
              {' '}
              <Badge $tone={TONE[r.direction] || 'info'}>{t(`${NS}.direction.${r.direction}`)}</Badge>
            </span>
          );
        },
      },
    },
    {
      name: 'selfStatus',
      label: t(`${NS}.col.evaluationState`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          return (
            <Muted>
              {`${t(`${NS}.col.self`)}: ${t(`${NS}.status.${r.selfStatus}`)} · ${t(`${NS}.col.manager`)}: ${t(`${NS}.status.${r.managerStatus}`)}`}
            </Muted>
          );
        },
      },
    },
    {
      name: 'raterCompleted',
      label: t(`${NS}.col.coverage360`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          return r.raterTotal ? `${formatNum(r.raterCompleted, lang, 0)} / ${formatNum(r.raterTotal, lang, 0)}` : dash;
        },
      },
    },
    { name: 'ackStatus', label: t(`${NS}.col.ack`), options: { sort: false, filter: false, customBodyRender: (v) => t(`${NS}.ack.${v || 'not_required'}`) } },
    {
      name: 'participantId',
      label: t(`${NS}.col.actions`),
      options: {
        sort: false, filter: false,
        customBodyRender: (v, meta) => {
          const r = rowOf(meta);
          const actions = [
            { type: 'open', label: t(`${NS}.rowAction.profile`), onClick: () => history.push(profileLink(r.employeeId, filters.periodId, 'meetings')) },
          ];
          if (r.gap !== null && r.gap !== undefined) {
            actions.push(isHr
              ? { type: 'reschedule', label: t(`${NS}.rowAction.expectationTalk`), onClick: () => setActionFor(r) }
              : { type: 'reschedule', label: t(`${NS}.rowAction.oneOnOne`), onClick: () => history.push(ONE_ON_ONES_PATH) });
          }
          return <OptionsMenu row={r} actions={actions} />;
        },
      },
    },
  ];

  return (
    <Wrap data-panel="employees">
      {pairOnly || effective.sort === 'gap' ? <Note data-note="pairs-only">{t(`${NS}.list.pairsOnly`)}</Note> : null}
      {panel.error ? (
        <>
          <LoadErrorBanner message={t('performance.operations.panel.error')} />
          <Button variant="outline" onClick={panel.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
        </>
      ) : null}
      <DataTable
        title={t(`${NS}.list.title`)}
        fetching={panel.loading}
        columns={columns}
        data={rows}
        totalRowCount={total}
        pageIndex={effective.pageNumber - 1}
        pageSize={effective.pageSize}
        onTableChanged={onTableChanged}
        onRowClick={(row) => history.push(profileLink(row.employeeId, filters.periodId, 'meetings'))}
        customIdSelector="participantId"
        disableFilters
        tableBodyMaxHeight="none"
        toolbarActions={(
          <Button variant="outline" onClick={handleExport} disabled={exporting || total === 0} data-action="export">
            <Download size={14} />
            {' '}
            {exporting ? t('common.loading') : t(`${NS}.list.export`)}
          </Button>
        )}
      />
      {isHr ? (
        <ActionFormDrawer
          open={!!actionFor}
          defaults={actionDefaults}
          onClose={() => setActionFor(null)}
          onSave={async (payload) => {
            const ok = await createAction(payload);
            if (ok) setActionFor(null);
            return ok;
          }}
        />
      ) : null}
    </Wrap>
  );
}
